import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

// 通用 API 响应类型
export interface ApiResponse<T = any> {
  code: number;
  msg: string;
  data: T;
}

// 创建 Axios 实例
const createApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // 请求拦截器
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // 可以在这里添加认证 token
      // const token = localStorage.getItem('token');
      // if (token) {
      //   config.headers = {
      //     ...config.headers,
      //     Authorization: `Bearer ${token}`,
      //   };
      // }
      
      console.log('发送请求:', config.method?.toUpperCase(), config.url);
      return config;
    },
    (error: AxiosError) => {
      console.error('请求拦截器错误:', error);
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      console.log('收到响应:', response.status, response.config.url);
      
      // 检查业务状态码
      if (response.data.code === 200) {
        return response;
      } else {
        // 业务错误
        const error = new Error(response.data.msg || '业务处理失败');
        return Promise.reject(error);
      }
    },
    (error: AxiosError) => {
      console.error('响应拦截器错误:', error);
      
      // 处理不同类型的错误
      if (error.response) {
        // 服务器响应了错误状态码
        const status = error.response.status;
        const message = (error.response.data as any)?.msg || `请求失败 (${status})`;
        
        switch (status) {
          case 400:
            return Promise.reject(new Error('请求参数错误'));
          case 401:
            return Promise.reject(new Error('未授权，请重新登录'));
          case 403:
            return Promise.reject(new Error('禁止访问'));
          case 404:
            return Promise.reject(new Error('请求的资源不存在'));
          case 500:
            return Promise.reject(new Error('服务器内部错误'));
          default:
            return Promise.reject(new Error(message));
        }
      } else if (error.request) {
        // 请求已发出但没有收到响应
        return Promise.reject(new Error('网络连接失败，请检查网络'));
      } else {
        // 其他错误
        return Promise.reject(new Error(error.message || '请求失败'));
      }
    }
  );

  return instance;
};

// 导出配置好的 Axios 实例
export const api = createApiInstance();

// 导出通用请求方法
export const request = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    api.get(url, config).then(response => response.data),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    api.post(url, data, config).then(response => response.data),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    api.put(url, data, config).then(response => response.data),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    api.delete(url, config).then(response => response.data),
};
